import React, { createContext, useContext, useState, useCallback } from 'react';

const BatchOperationsContext = createContext();

export const useBatchOperations = () => {
  const context = useContext(BatchOperationsContext);
  if (!context) {
    throw new Error('useBatchOperations must be used within a BatchOperationsProvider');
  }
  return context;
};

export const BatchOperationsProvider = ({ children }) => {
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // Alle ausgewählten Task-IDs als Array
  const getSelectedTaskIds = useCallback(() => {
    return Array.from(selectedTaskIds);
  }, [selectedTaskIds]);

  // Anzahl der ausgewählten Tasks
  const getSelectedCount = useCallback(() => {
    return selectedTaskIds.size;
  }, [selectedTaskIds]);

  // Task auswählen/abwählen
  const toggleTaskSelection = useCallback((taskId, isSelected) => {
    setSelectedTaskIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(taskId);
      } else {
        newSet.delete(taskId);
      }
      return newSet;
    });
  }, []);

  // Alle Tasks auswählen
  const selectAllTasks = useCallback((taskIds) => {
    setSelectedTaskIds(new Set(taskIds));
  }, []);

  // Alle Auswahlen löschen
  const clearSelection = useCallback(() => {
    setSelectedTaskIds(new Set());
  }, []);

  // Prüfen ob Task ausgewählt ist
  const isTaskSelected = useCallback((taskId) => {
    return selectedTaskIds.has(taskId);
  }, [selectedTaskIds]);

  // Auswahl-Modus aktivieren/deaktivieren
  const toggleSelectionMode = useCallback(() => {
    const newMode = !isSelectionMode;
    setIsSelectionMode(newMode);
    
    // Wenn Auswahl-Modus deaktiviert wird, Auswahl löschen
    if (!newMode) {
      clearSelection();
    }
  }, [isSelectionMode, clearSelection]);

  // Batch-Modal öffnen
  const openBatchModal = useCallback(() => {
    if (selectedTaskIds.size > 0) {
      setShowBatchModal(true);
    }
  }, [selectedTaskIds.size]);

  // Batch-Modal schließen
  const closeBatchModal = useCallback(() => {
    setShowBatchModal(false);
  }, []);

  // Alle Tasks eines Panels togglen (invertieren)
  const togglePanelTasks = useCallback((taskIds) => {
    setSelectedTaskIds(prev => {
      const newSet = new Set(prev);
      
      // Jede einzelne Task invertieren
      taskIds.forEach(id => {
        if (prev.has(id)) {
          // Task ist ausgewählt -> abwählen
          newSet.delete(id);
        } else {
          // Task ist nicht ausgewählt -> auswählen
          newSet.add(id);
        }
      });
      
      return newSet;
    });
  }, []);

  // Batch-Operation ausführen
  const executeBatchOperation = useCallback((operationData, updateTasksCallback, deleteTasksCallback, assignStudentsCallback, copyMoveTasksCallback) => {
    console.log('Executing batch operation:', operationData);
    
    if (operationData.operation === 'edit-properties' && operationData.updates && updateTasksCallback) {
      // Wende Updates auf alle ausgewählten Tasks an
      updateTasksCallback(operationData.taskIds, operationData.updates);
    } else if (operationData.operation === 'delete' && deleteTasksCallback) {
      // Lösche alle ausgewählten Tasks
      deleteTasksCallback(operationData.taskIds);
    } else if (operationData.operation === 'assign-students' && operationData.settings && assignStudentsCallback) {
      // Weise Lernende zu allen ausgewählten Tasks zu oder entferne sie
      assignStudentsCallback(operationData.taskIds, operationData.settings);
    } else if ((operationData.operation === 'duplicate' || operationData.operation === 'move') && operationData.settings && copyMoveTasksCallback) {
      // Kopiere oder verschiebe Tasks
      copyMoveTasksCallback(operationData.taskIds, operationData.settings);
    }
    
    // Nach der Operation Auswahl löschen
    clearSelection();
    setShowBatchModal(false);
  }, [clearSelection]);

  const value = {
    // State
    selectedTaskIds,
    isSelectionMode,
    showBatchModal,
    
    // Getter
    getSelectedTaskIds,
    getSelectedCount,
    isTaskSelected,
    
    // Actions
    toggleTaskSelection,
    selectAllTasks,
    clearSelection,
    toggleSelectionMode,
    togglePanelTasks,
    openBatchModal,
    closeBatchModal,
    executeBatchOperation,
    
    // Modal controls
    setShowBatchModal
  };

  return (
    <BatchOperationsContext.Provider value={value}>
      {children}
    </BatchOperationsContext.Provider>
  );
};

export default BatchOperationsContext;